// app/src/features/reforecast/useMarkMoments.js
import { liveSentinelMoment } from "../../lib/forecast/reforecastEntries.js";
import { R_START } from "../../lib/timeline/geometry.js";

/**
 * The forecast-moment axis marks and the mark the cursor is nearest: each entry's moment
 * (`fcMoments`, also the `searchMoments` the guide lines are drawn for), its x in timeline-local
 * px (`fcMarkX`), the mark within snapping distance of the cursor (`nearestMarkIso`), the mark
 * the gold guide line and the rewind click act on (`activeMarkIso`: the hovered mark, else the
 * viewed one) and the live forecast sentinel's moment (`liveSentinelIso`). Plain per-render
 * derivations — no React hook inside, so it registers nothing; called where `AppInner` built
 * `fcMoments`, after the timeline geometry whose `viewLeft` / `ppd` / `axisHoverX` it reads.
 *
 * The arrivals store (`useArrivals`, `acknowledgeArrival`) stays in `AppInner` with the effect
 * that clears a mark's "new" ring (C-23: `features/**` never imports the store).
 *
 * @param {object} deps
 * @param {Array} deps.reforecastEntries  the scrub list (useReforecastWiring's)
 * @param {string|null} deps.viewMomentIso  the scrubbed moment, or null when live
 * @param {number} deps.viewLeft  the timeline's left edge in days since R_START (the geometry's)
 * @param {number} deps.ppd  px per day (the geometry's)
 * @param {number|null} deps.axisHoverX  the cursor X in timeline-local px, or null (the geometry's)
 * @param {object|null} deps.hov  the hovered pie / trigger, or null (AppInner's hover state)
 */
export function useMarkMoments({
  reforecastEntries,
  viewMomentIso,
  viewLeft,
  ppd,
  axisHoverX,
  hov,
}) {
  // Forecast-moment axis marks + the mark the cursor is currently nearest. The gold snapshot guide
  // line and the rewind click are driven off `activeMarkIso`, so hovering ANYWHERE in a mark's
  // column (forecast area, date axis, or the signals below) surfaces the same clickable line, not
  // just the little hash. `axisHoverX` is the cursor X in timeline-local px (already
  // zoom-corrected), tracked on the container.
  //
  // Marks use each snapshot's `generatedAt`, the SAME key the snapshot lookups use (viewSnap /
  // selectSnapshotByMoment / logForecasts), so a clicked mark resolves directly to its snapshot.
  // The LAST entry is always the live marker (appended as a sentinel — its moment can fall back
  // to createdAt and sort earlier, so don't assume the array is strictly chronological), and
  // clicking it returns to latest.
  const fcMoments = reforecastEntries.map((e) => e.moment);
  const fcMarkX = (iso) => {
    // Date.parse avoids a Date allocation because fcMarkX runs per mark per render, and densely on
    // hover.
    const ms = Date.parse(iso);
    return isNaN(ms) ? null : ((ms - R_START) / 86400000 - viewLeft) * ppd;
  };
  // The live/newest fire is a first-class target: clicking it returns to latest and hovering it
  // clears its arrival ring.
  const searchMoments = fcMoments;
  const nearestMarkIso = (() => {
    // Pie charts take priority over the snapshot guide line: while the cursor is
    // over ANY pie/trigger circle (every such hover sets `hov`), suppress the
    // nearest-mark snap so the gold line does NOT rise and its click band can't
    // steal the pie's click — letting you click the pie directly. Hovering anywhere
    // else (spine, axis, signals), `hov` is null and the line works as normal.
    if (hov) return null;
    if (axisHoverX == null || !searchMoments.length) return null;
    let best = null,
      bestD = Infinity;
    const xs = [];
    for (const iso of searchMoments) {
      const x = fcMarkX(iso);
      if (x == null) continue;
      xs.push(x);
      const d = Math.abs(x - axisHoverX);
      if (d < bestD) {
        bestD = d;
        best = iso;
      }
    }
    // Tightest neighbor spacing among the candidate marks — computed from SORTED
    // x's (searchMoments isn't guaranteed time-ordered, so consecutive raw entries
    // aren't necessarily adjacent on screen).
    xs.sort((a, b) => a - b);
    let minGap = Infinity;
    for (let i = 1; i < xs.length; i++) minGap = Math.min(minGap, xs[i] - xs[i - 1]);
    // The radius is about half the tightest neighbor gap, so packed hourly marks leave no dead zone
    // where nothing lights. Nearest-wins still guarantees exactly ONE mark is ever selected.
    const cap = Math.max(8, Math.min(14, minGap / 2));
    return bestD <= cap ? best : null;
  })();
  // Cursor-hover WINS over the current selection, so you can hover + click a different mark without
  // first returning to the latest.
  const activeMarkIso = nearestMarkIso || viewMomentIso;
  // The live sentinel, NOT the list tail, is the "latest FORECAST" moment: the tail becomes the
  // newest RESOLUTION once resolution entries are appended. Only the live forecast sentinel is
  // labelled "Latest forecast"; a resolution mark is labelled as its verdict.
  const liveSentinelIso = liveSentinelMoment(reforecastEntries);
  return { fcMoments, fcMarkX, searchMoments, nearestMarkIso, activeMarkIso, liveSentinelIso };
}
